<template>
  <div class="ytcid-video">
    <div class="vx-col w-full mb-base">
      <vx-card class="w-full ytcid-video__card">
        <div class="yt-crumb">
          <router-link to="/youtube-cid" class="yt-crumb__back">
            <i class="el-icon-arrow-left"></i>
            <span>검색</span>
          </router-link>
        </div>

        <header class="yt-hero">
          <div class="yt-hero__main">
            <h1 class="yt-hero__title">
              <span class="yt-hero__label">동영상</span>
              <span class="yt-mono">{{ videoId }}</span>
            </h1>
          </div>
          <div class="yt-hero__aside">
            <a
              :href="`https://youtube.com/watch?v=${videoId}`"
              target="_blank"
              rel="noopener"
              class="yt-hero__out"
            >
              YouTube에서 열기
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </header>

        <div v-if="error" class="yt-error">
          <span>이 동영상의 소유권 주장을 불러오지 못했습니다.</span>
          <button type="button" class="yt-error__retry" @click="load">다시 시도</button>
        </div>

        <div v-if="loading" class="yt-skel" aria-hidden="true">
          <div v-for="n in 3" :key="n" class="yt-skel__row">
            <span class="yt-skel__bar yt-skel__bar--id"></span>
            <span class="yt-skel__bar yt-skel__bar--title"></span>
            <span class="yt-skel__bar yt-skel__bar--asset"></span>
            <span class="yt-skel__bar yt-skel__bar--type"></span>
            <span class="yt-skel__bar yt-skel__bar--policy"></span>
            <span class="yt-skel__bar yt-skel__bar--status"></span>
            <span class="yt-skel__bar yt-skel__bar--date"></span>
            <span class="yt-skel__bar yt-skel__bar--actions"></span>
          </div>
        </div>

        <div v-else-if="!claims.length" class="yt-empty">
          <p class="yt-empty__lead">소유권 주장이 없습니다.</p>
        </div>

        <div v-else class="yt-table">
          <div class="yt-table__head">
            <div class="yt-cell yt-cell--id">소유권 주장</div>
            <div class="yt-cell yt-cell--title">곡명 / 아티스트</div>
            <div class="yt-cell yt-cell--asset">저작물</div>
            <div class="yt-cell yt-cell--type">유형</div>
            <div class="yt-cell yt-cell--policy">정책</div>
            <div class="yt-cell yt-cell--status">상태</div>
            <div class="yt-cell yt-cell--date">생성일</div>
            <div class="yt-cell yt-cell--actions">작업</div>
          </div>

          <div
            v-for="c in claims"
            :key="c.id"
            class="yt-row"
            :class="{ 'is-active': isActive(c) }"
          >
            <div class="yt-cell yt-cell--id" @click="openClaim(c.id)">
              <IdMono :value="c.id" :copyable="false" :truncate="22"></IdMono>
            </div>
            <div class="yt-cell yt-cell--title" @click="openClaim(c.id)">
              <div class="yt-title-text">{{ c.assetTitle || '제목 없음' }}</div>
              <div v-if="c.assetArtist" class="yt-title-sub">{{ c.assetArtist }}</div>
            </div>
            <div class="yt-cell yt-cell--asset" @click="openClaim(c.id)">
              <IdMono :value="c.assetId || ''" :copyable="false" :truncate="18"></IdMono>
            </div>
            <div class="yt-cell yt-cell--type" @click="openClaim(c.id)">
              <span
                v-if="c.assetType || c.contentType"
                class="yt-type-pill"
                :class="`yt-type-pill--${typeKey(c)}`"
              >{{ typeLabel(c) }}</span>
              <span v-else class="yt-type-pill yt-type-pill--unknown">—</span>
            </div>
            <div class="yt-cell yt-cell--policy" @click="openClaim(c.id)">
              <div v-if="policyActions(c).length" class="yt-policy-actions">
                <StatusPill
                  v-for="action in policyActions(c)"
                  :key="action"
                  :variant="action"
                ></StatusPill>
              </div>
              <span v-else-if="policyName(c)" class="yt-policy">{{ policyName(c) }}</span>
              <span v-else class="yt-policy yt-policy--missing">—</span>
              <div v-if="policyActions(c).length && policyName(c)" class="yt-policy-name">{{ policyName(c) }}</div>
            </div>
            <div class="yt-cell yt-cell--status" @click="openClaim(c.id)">
              <StatusPill :variant="statusVariant(c.status)"></StatusPill>
            </div>
            <div class="yt-cell yt-cell--date" :title="absoluteTime(c.timeCreated)" @click="openClaim(c.id)">
              {{ relativeTime(c.timeCreated) }}
            </div>
            <div class="yt-cell yt-cell--actions" @click.stop>
              <button
                v-if="isActive(c)"
                type="button"
                class="yt-action yt-action--danger"
                :disabled="releasingId === c.id"
                @click.stop="askRelease(c)"
              >
                <span>{{ releasingId === c.id ? '처리 중…' : '해제' }}</span>
              </button>
              <a
                :href="cmsClaimUrl(c.id)"
                target="_blank"
                rel="noopener"
                class="yt-action yt-action--link"
                @click.stop
              >
                CMS<span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
        </div>
      </vx-card>
    </div>

    <ConfirmActionDialog
      :visible.sync="releaseDialogOpen"
      title="소유권 주장 해제"
      :confirm-token="releaseTarget ? releaseTarget.id : ''"
      confirm-label="소유권 주장 해제"
      :dangerous="true"
      :submitting="!!releasingId"
      @confirm="onRelease"
    >
      <template #subject v-if="releaseTarget">
        소유권 주장 <strong>{{ releaseTarget.id }}</strong> · 동영상 <strong>{{ videoId }}</strong>
      </template>
      <template #diff v-if="releaseTarget">
        <div style="font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;font-size:12px;line-height:1.6;">
          <div style="color:#EA5455;">− status: {{ releaseTarget.status }}</div>
          <div style="color:#28C76F;">+ status: inactive</div>
        </div>
      </template>
    </ConfirmActionDialog>
  </div>
</template>

<script>
import { YoutubeCidApi, YOUTUBE_CMS_CONTENT_OWNER_ID } from './api.js'
import IdMono from './components/IdMono.vue'
import StatusPill from './components/StatusPill.vue'
import ConfirmActionDialog from './components/ConfirmActionDialog.vue'
import { toLocalDateTime } from './dateFormat.js'

const STATUS_VARIANT = {
  active: 'active',
  inactive: 'inactive',
  pending: 'pending',
  takedown: 'takedown',
  appealed: 'appealed',
  disputed: 'disputed',
}

const ASSET_TYPE_LABELS = {
  sound_recording: '사운드 레코딩 (SR)',
  art_track_video: '아트 트랙 (AT)',
  composition: '음악작품 공유',
  music_video: '뮤직 비디오',
  movie: '영화',
  episode: '에피소드',
  tv_episode: 'TV 에피소드',
  tv_season: 'TV 시즌',
  show: '프로그램',
  web: '웹',
  general: '일반',
  video_game: '비디오 게임',
}

const CLAIM_CONTENT_TYPE_LABEL = {
  audiovisual: 'AV',
  audio: '음원',
  visual: '영상',
  web: '웹',
}

export default {
  name: 'YoutubeCidVideoClaimsList',
  components: { IdMono, StatusPill, ConfirmActionDialog },
  data() {
    return {
      loading: false,
      claims: [],
      error: null,
      releaseDialogOpen: false,
      releaseTarget: null,
      releasingId: '',
    }
  },
  computed: {
    videoId() {
      return this.$route.params.id || ''
    },
  },
  watch: {
    '$route.params.id': { immediate: true, handler() { this.load() } },
  },
  methods: {
    async load() {
      if (!this.videoId) {
        this.claims = []
        return
      }
      this.loading = true
      this.error = null
      try {
        const data = await YoutubeCidApi.searchClaims({ videoId: this.videoId, maxResults: '50' })
        this.claims = (data && data.items) || []
      } catch (e) {
        this.error = e
        this.claims = []
      } finally {
        this.loading = false
      }
    },
    openClaim(id) {
      this.$router.push(`/youtube-cid/claim/${id}`)
    },
    isActive(c) {
      return (c.status || '').toLowerCase() === 'active'
    },
    statusVariant(s) {
      return STATUS_VARIANT[(s || '').toLowerCase()] || 'inactive'
    },
    typeLabel(c) {
      const at = c && c.assetType
      if (at) {
        const k = String(at).toLowerCase()
        return ASSET_TYPE_LABELS[k] || String(at).replace(/_/g, ' ').toUpperCase()
      }
      const ct = c && c.contentType
      if (!ct) return '—'
      const k = String(ct).toLowerCase()
      return CLAIM_CONTENT_TYPE_LABEL[k] || ct
    },
    typeKey(c) {
      const t = (c && (c.assetType || c.contentType)) || 'unknown'
      return String(t).toLowerCase().replace(/[^a-z0-9]+/g, '-')
    },
    policyName(c) {
      const p = c && c.appliedPolicy
      if (!p) return ''
      return p.name || p.id || ''
    },
    policyActions(c) {
      const rules = c && c.appliedPolicy && c.appliedPolicy.rules
      if (!Array.isArray(rules) || !rules.length) return []
      const seen = new Set()
      const out = []
      for (const r of rules) {
        const a = r && typeof r.action === 'string' ? r.action.toLowerCase() : ''
        if (!a || seen.has(a)) continue
        if (a !== 'block' && a !== 'monetize' && a !== 'track') continue
        const territoryType = r.conditions && r.conditions.requiredTerritories && r.conditions.requiredTerritories.type
        if (territoryType === 'exclude') continue
        seen.add(a)
        out.push(a)
      }
      return out
    },
    cmsClaimUrl(claimId) {
      if (!claimId || !this.videoId) return ''
      return `https://studio.youtube.com/video/${encodeURIComponent(this.videoId)}/claims/claims/${encodeURIComponent(claimId)}/edit?o=${YOUTUBE_CMS_CONTENT_OWNER_ID}`
    },
    absoluteTime(t) {
      if (!t) return ''
      return toLocalDateTime(t) || t
    },
    relativeTime(t) {
      if (!t) return '—'
      const d = new Date(t)
      if (Number.isNaN(d.getTime())) return '—'
      const diff = Date.now() - d.getTime()
      const sec = Math.floor(diff / 1000)
      if (sec < 60) return '방금 전'
      const min = Math.floor(sec / 60)
      if (min < 60) return `${min}분 전`
      const hr = Math.floor(min / 60)
      if (hr < 24) return `${hr}시간 전`
      const day = Math.floor(hr / 24)
      if (day < 30) return `${day}일 전`
      const mon = Math.floor(day / 30)
      if (mon < 12) return `${mon}개월 전`
      return `${Math.floor(mon / 12)}년 전`
    },
    askRelease(c) {
      this.releaseTarget = c
      this.releaseDialogOpen = true
    },
    async onRelease() {
      if (!this.releaseTarget) return
      const id = this.releaseTarget.id
      this.releasingId = id
      try {
        await YoutubeCidApi.updateClaim(id, { status: 'inactive' })
        this.releaseDialogOpen = false
        this.releaseTarget = null
        await this.load()
      } finally {
        this.releasingId = ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$yt-border:        rgba(0, 0, 0, 0.08);
$yt-border-strong: rgba(0, 0, 0, 0.14);
$yt-text:          #0f0f0f;
$yt-text-muted:    rgba(15, 15, 15, 0.6);
$yt-text-faint:    rgba(15, 15, 15, 0.42);
$yt-bg-hover:      rgba(15, 15, 15, 0.04);
$yt-accent:        #065fd4;
$yt-danger:        #c4302b;

.ytcid-video {
  font-family: 'Roboto', 'YouTube Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: $yt-text;
}
.ytcid-video__card ::v-deep .vx-card__body {
  padding: 0;
}

.yt-mono {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.85em;
  letter-spacing: 0.01em;
}

.yt-crumb {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 24px;
  border-bottom: 1px solid $yt-border;
}
.yt-crumb__back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: $yt-text-muted;
  text-decoration: none;
  transition: color 120ms ease;
  i { font-size: 14px; }
  &:hover { color: $yt-text; }
}

.yt-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 32px;
  border-bottom: 1px solid $yt-border;
}
.yt-hero__main { min-width: 0; }
.yt-hero__title {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 0;
  font-size: clamp(20px, 2.2vw, 28px);
  font-weight: 500;
  line-height: 1.2;
  min-width: 0;
}
.yt-hero__label {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $yt-text-muted;
}
.yt-hero__aside { flex-shrink: 0; }
.yt-hero__out {
  font-size: 13px;
  font-weight: 500;
  color: $yt-accent;
  text-decoration: none;
  &:hover { text-decoration: underline; }
  span { margin-left: 2px; }
}

.yt-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 32px;
  font-size: 13px;
  color: $yt-danger;
  background: rgba(196, 48, 43, 0.04);
  border-bottom: 1px solid rgba(196, 48, 43, 0.18);
}
.yt-error__retry {
  background: transparent;
  border: 0;
  color: $yt-danger;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
}

.yt-empty {
  padding: 64px 32px 80px;
  max-width: 560px;
}
.yt-empty__lead {
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  color: $yt-text;
}

.yt-table { display: block; }
.yt-table__head,
.yt-row {
  display: grid;
  grid-template-columns:
    minmax(180px, 1.1fr)
    minmax(160px, 1.2fr)
    minmax(150px, 1fr)
    140px
    100px
    92px
    96px
    140px;
  gap: 16px;
  align-items: center;
  padding: 0 32px;
}
.yt-table__head {
  height: 40px;
  border-bottom: 1px solid $yt-border;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $yt-text-muted;
}
.yt-row {
  position: relative;
  min-height: 60px;
  border-bottom: 1px solid $yt-border;
  transition: background-color 80ms ease;
  &:hover { background: $yt-bg-hover; }
  &.is-active::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 2px;
    background: $yt-accent;
  }
}
.yt-cell {
  min-width: 0;
  font-size: 13px;
  color: $yt-text;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  &--date {
    color: $yt-text-muted;
    font-variant-numeric: tabular-nums;
  }
  &--actions {
    cursor: default;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }
}
.yt-title-text {
  font-weight: 500;
  color: $yt-text;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.yt-title-sub {
  font-size: 12px;
  color: $yt-text-muted;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.yt-type-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 3px 8px;
  border-radius: 10px;
  line-height: 1.4;
  border: 1px solid transparent;
  background: rgba(15, 15, 15, 0.06);
  color: $yt-text-muted;
  white-space: nowrap;
  &--sound-recording,
  &--audio {
    background: rgba(6, 95, 212, 0.1);
    color: #065fd4;
    border-color: rgba(6, 95, 212, 0.25);
  }
  &--art-track-video {
    background: rgba(142, 36, 170, 0.1);
    color: #8e24aa;
    border-color: rgba(142, 36, 170, 0.25);
  }
  &--audiovisual,
  &--music-video {
    background: rgba(216, 27, 96, 0.1);
    color: #d81b60;
    border-color: rgba(216, 27, 96, 0.25);
  }
  &--composition {
    background: rgba(57, 73, 171, 0.1);
    color: #3949ab;
    border-color: rgba(57, 73, 171, 0.25);
  }
  &--movie {
    background: rgba(230, 81, 0, 0.1);
    color: #e65100;
    border-color: rgba(230, 81, 0, 0.25);
  }
  &--tv-episode,
  &--episode,
  &--tv-season,
  &--show {
    background: rgba(46, 125, 50, 0.1);
    color: #2e7d32;
    border-color: rgba(46, 125, 50, 0.25);
  }
  &--visual {
    background: rgba(216, 27, 96, 0.1);
    color: #d81b60;
    border-color: rgba(216, 27, 96, 0.25);
  }
  &--web {
    background: rgba(0, 131, 143, 0.1);
    color: #00838f;
    border-color: rgba(0, 131, 143, 0.25);
  }
  &--video-game {
    background: rgba(255, 143, 0, 0.12);
    color: #ef6c00;
    border-color: rgba(255, 143, 0, 0.3);
  }
  &--general {
    background: rgba(97, 97, 97, 0.1);
    color: #616161;
    border-color: rgba(97, 97, 97, 0.25);
  }
  &--unknown {
    background: rgba(15, 15, 15, 0.04);
    color: $yt-text-faint;
    border-color: $yt-border;
  }
}

.yt-policy { color: $yt-text; }
.yt-policy--missing { color: $yt-text-faint; }
.yt-policy-actions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.yt-policy-name {
  font-size: 11px;
  color: $yt-text-faint;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.yt-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border-radius: 13px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  background: #fff;
  border: 1px solid $yt-border-strong;
  color: $yt-text;
  text-decoration: none;
  white-space: nowrap;
  transition: background 80ms ease, color 80ms ease, border-color 80ms ease;
  &:hover { background: $yt-bg-hover; }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &--danger {
    color: $yt-danger;
    border-color: rgba(196, 48, 43, 0.3);
    &:hover { background: rgba(196, 48, 43, 0.06); border-color: rgba(196, 48, 43, 0.5); }
  }
  &--link {
    color: $yt-accent;
    border-color: rgba(6, 95, 212, 0.3);
    span { font-size: 11px; margin-left: 2px; }
    &:hover { background: rgba(6, 95, 212, 0.06); border-color: rgba(6, 95, 212, 0.5); }
  }
}

.yt-skel { padding: 8px 0; }
.yt-skel__row {
  display: grid;
  grid-template-columns:
    minmax(180px, 1.1fr)
    minmax(160px, 1.2fr)
    minmax(150px, 1fr)
    140px
    100px
    92px
    96px
    140px;
  gap: 16px;
  align-items: center;
  padding: 0 32px;
  height: 60px;
  border-bottom: 1px solid $yt-border;
}
.yt-skel__bar {
  height: 12px;
  border-radius: 3px;
  background: linear-gradient(90deg, rgba(15,15,15,0.05) 0%, rgba(15,15,15,0.09) 50%, rgba(15,15,15,0.05) 100%);
  background-size: 200% 100%;
  animation: yt-shimmer 1.2s ease-in-out infinite;
}
.yt-skel__bar--id      { width: 70%; }
.yt-skel__bar--title   { width: 80%; }
.yt-skel__bar--asset   { width: 65%; }
.yt-skel__bar--type    { width: 36px; }
.yt-skel__bar--policy  { width: 60%; }
.yt-skel__bar--status  { width: 50px; }
.yt-skel__bar--date    { width: 60%; }
.yt-skel__bar--actions { width: 70%; }

@keyframes yt-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .yt-skel__bar { animation: none; }
  .yt-crumb__back, .yt-row { transition: none; }
}

@media (max-width: 1100px) {
  .yt-table__head, .yt-row, .yt-skel__row {
    grid-template-columns: 1.3fr 1.6fr 1fr 64px 92px 140px;
    gap: 12px;
    .yt-cell--policy, .yt-cell--date { display: none; }
    .yt-skel__bar--policy, .yt-skel__bar--date { display: none; }
  }
}

@media (max-width: 900px) {
  .yt-hero { padding: 20px 20px; }
  .yt-crumb { padding: 0 16px; }
  .yt-table__head, .yt-row, .yt-skel__row {
    padding: 0 20px;
    grid-template-columns: 1.6fr 1fr 64px 92px 130px;
    .yt-cell--id, .yt-cell--asset { display: none; }
    .yt-skel__bar--id, .yt-skel__bar--asset { display: none; }
  }
  .yt-empty { padding: 48px 20px 64px; }
}
</style>
